// Chart of Accounts service - moved from database.js
import { 
  collection, 
  doc, 
  getDocs, 
  getDoc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  query, 
  where, 
  orderBy, 
  serverTimestamp, 
  db 
} from '../../utils/database/firebase-config';

export const chartOfAccountsService = {
  async getChartOfAccounts(companyId, includeInactive = false) {
    const accountsRef = collection(db, 'chartOfAccounts');
    let q = query(accountsRef, where('companyId', '==', companyId), orderBy('accountCode'));
    
    const snapshot = await getDocs(q);
    let accounts = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    
    // ✅ Remove duplicates - Keep first occurrence of each account code
    const seenCodes = new Map();
    const duplicateIds = [];
    
    accounts = accounts.filter(account => {
      if (seenCodes.has(account.accountCode)) {
        console.warn(`Duplicate account code found: ${account.accountCode}, removing duplicate`);
        duplicateIds.push(account.id);
        return false;
      }
      seenCodes.set(account.accountCode, account.id);
      return true;
    });
    
    // Clean up duplicates from database (async, don't wait)
    if (duplicateIds.length > 0) {
      this.cleanupDuplicateAccounts(duplicateIds).catch(console.error);
    }
    
    // Filter inactive accounts if requested
    if (!includeInactive) {
      accounts = accounts.filter(acc => acc.isActive !== false);
    }
    
    // Calculate current balances from journal entries
    accounts = await this.calculateAccountBalances(companyId, accounts);
    
    return accounts;
  },

  async getAccountsByType(companyId, accountType, includeInactive = false) {
    const accounts = await this.getChartOfAccounts(companyId, includeInactive);
    return accounts.filter(acc => acc.accountType === accountType);
  },

  async getAccountHierarchy(companyId) {
    const accounts = await this.getChartOfAccounts(companyId);
    return this.buildAccountHierarchy(accounts);
  },

  buildAccountHierarchy(accounts) {
    const hierarchy = {
      asset: { name: 'Assets', accounts: [], totalBalance: 0 },
      liability: { name: 'Liabilities', accounts: [], totalBalance: 0 },
      equity: { name: 'Equity', accounts: [], totalBalance: 0 },
      revenue: { name: 'Revenue', accounts: [], totalBalance: 0 },
      expense: { name: 'Expenses', accounts: [], totalBalance: 0 }
    };

    accounts.forEach(account => {
      if (hierarchy[account.accountType]) {
        hierarchy[account.accountType].accounts.push(account);
        hierarchy[account.accountType].totalBalance += account.currentBalance || 0;
      }
    });

    // Sort accounts within each type by account code
    Object.keys(hierarchy).forEach(type => {
      hierarchy[type].accounts.sort((a, b) => a.accountCode.localeCompare(b.accountCode));
    });

    return hierarchy;
  },

  async calculateAccountBalances(companyId, accounts) {
    try {
      // Import journal entries service to calculate balances
      const { journalEntriesService } = await import('./journal-entries');
      const journalEntries = await journalEntriesService.getJournalEntries(companyId);
      
      // Calculate balances for each account
      const accountBalances = {};
      
      journalEntries.forEach(entry => {
        if (entry.status === 'posted' && entry.lineItems) {
          entry.lineItems.forEach(lineItem => {
            const accountCode = lineItem.accountCode;
            if (!accountBalances[accountCode]) {
              accountBalances[accountCode] = { debit: 0, credit: 0 };
            }
            accountBalances[accountCode].debit += lineItem.debit || 0;
            accountBalances[accountCode].credit += lineItem.credit || 0;
          });
        }
      });

      // Update account balances
      return accounts.map(account => {
        const balance = accountBalances[account.accountCode] || { debit: 0, credit: 0 };
        const netBalance = balance.debit - balance.credit;
        
        // Determine current balance based on normal balance type
        let currentBalance = 0;
        if (['asset', 'expense'].includes(account.accountType)) {
          // Debit normal balance
          currentBalance = (account.openingBalance || 0) + netBalance;
        } else {
          // Credit normal balance (liability, equity, revenue)
          currentBalance = (account.openingBalance || 0) - netBalance;
        }

        return {
          ...account,
          currentBalance,
          debitTotal: balance.debit,
          creditTotal: balance.credit,
          netMovement: netBalance
        };
      });
    } catch (error) {
      console.error('Error calculating account balances:', error);
      // Return accounts with opening balances only
      return accounts.map(account => ({
        ...account,
        currentBalance: account.openingBalance || 0,
        debitTotal: 0,
        creditTotal: 0,
        netMovement: 0
      }));
    }
  },
  async getAccount(accountId) {
    const accountRef = doc(db, 'chartOfAccounts', accountId);
    const snapshot = await getDoc(accountRef);
    if (snapshot.exists()) return { id: snapshot.id, ...snapshot.data() };
    return null;
  },
  async addAccount(accountData) {
    // ✅ Enhanced validation to prevent duplicates
    await this.validateAccountCode(accountData.companyId, accountData.accountCode);
    
    const accountsRef = collection(db, 'chartOfAccounts');
    const docRef = await addDoc(accountsRef, { 
      ...accountData, 
      createdAt: serverTimestamp(), 
      updatedAt: serverTimestamp(),
      isActive: accountData.isActive !== false, // Default to active
      openingBalance: accountData.openingBalance || 0,
      currentBalance: accountData.openingBalance || 0,
      allowManualEntries: accountData.allowManualEntries !== false, // Default to allow
      requireDepartment: accountData.requireDepartment || false,
      isTaxRelated: accountData.isTaxRelated || false,
      isVatAccount: accountData.isVatAccount || false,
      isZakatAccount: accountData.isZakatAccount || false
    });
    
    // Log account creation
    await this.logAccountChange(accountData.companyId, docRef.id, 'created', accountData.createdBy);
    
    return docRef.id;
  },

  async validateAccountCode(companyId, accountCode, excludeId = null) {
    const accountsRef = collection(db, 'chartOfAccounts');
    const q = query(accountsRef, 
      where('companyId', '==', companyId), 
      where('accountCode', '==', accountCode)
    );
    const snapshot = await getDocs(q);
    
    // ✅ Enhanced duplicate checking
    const duplicates = snapshot.docs.filter(doc => doc.id !== excludeId);
    if (duplicates.length > 0) {
      throw new Error(`Account code ${accountCode} already exists (${duplicates.length} duplicate${duplicates.length > 1 ? 's' : ''} found)`);
    }
    
    // Validate account code format (4 digits)
    if (!/^\d{4}$/.test(accountCode)) {
      throw new Error('Account code must be exactly 4 digits');
    }
    
    return true;
  },
  async updateAccount(accountId, accountData) {
    // Get current account data for validation and logging
    const currentAccount = await this.getAccount(accountId);
    if (!currentAccount) {
      throw new Error('Account not found');
    }
    
    // Validate account code if it's being changed
    if (accountData.accountCode && accountData.accountCode !== currentAccount.accountCode) {
      await this.validateAccountCode(currentAccount.companyId, accountData.accountCode, accountId);
    }
    
    const accountRef = doc(db, 'chartOfAccounts', accountId);
    await updateDoc(accountRef, { 
      ...accountData, 
      updatedAt: serverTimestamp() 
    });
    
    // Log account changes
    await this.logAccountChange(currentAccount.companyId, accountId, 'updated', accountData.updatedBy, {
      before: currentAccount,
      after: { ...currentAccount, ...accountData }
    });
    
    return true;
  },
  async deleteAccount(accountId, deletedBy) {
    // Check if account is being used in journal entries
    const isUsed = await this.checkAccountUsage(accountId);
    if (isUsed) {
      throw new Error('Cannot delete account that has been used in transactions. Consider deactivating instead.');
    }
    
    const currentAccount = await this.getAccount(accountId);
    if (!currentAccount) {
      throw new Error('Account not found');
    }
    
    // Log account deletion before deleting
    await this.logAccountChange(currentAccount.companyId, accountId, 'deleted', deletedBy, currentAccount);
    
    const accountRef = doc(db, 'chartOfAccounts', accountId);
    await deleteDoc(accountRef);
    return true;
  },

  async deactivateAccount(accountId, deactivatedBy) {
    return await this.updateAccount(accountId, { 
      isActive: false, 
      deactivatedAt: serverTimestamp(),
      deactivatedBy 
    });
  },

  async checkAccountUsage(accountId) {
    try {
      const account = await this.getAccount(accountId);
      if (!account) return false;
      
      // Check journal entries
      const { journalEntriesService } = await import('./journal-entries');
      const journalEntries = await journalEntriesService.getJournalEntries(account.companyId);
      
      return journalEntries.some(entry => 
        entry.lineItems && entry.lineItems.some(item => item.accountCode === account.accountCode)
      );
    } catch (error) {
      console.error('Error checking account usage:', error);
      return true; // Err on the side of caution
    }
  },

  async logAccountChange(companyId, accountId, action, userId, details = null) {
    try {
      const logsRef = collection(db, 'accountChangeLogs');
      await addDoc(logsRef, {
        companyId,
        accountId,
        action,
        userId,
        details,
        timestamp: serverTimestamp()
      });
    } catch (error) {
      console.error('Error logging account change:', error);
      // Don't throw error as this is non-critical
    }
  },

  async getAccountChangeLogs(companyId, accountId = null) {
    const logsRef = collection(db, 'accountChangeLogs');
    let q = query(logsRef, where('companyId', '==', companyId), orderBy('timestamp', 'desc'));
    
    if (accountId) {
      q = query(logsRef, 
        where('companyId', '==', companyId),
        where('accountId', '==', accountId),
        orderBy('timestamp', 'desc')
      );
    }
    
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  },
  async initializeDefaultChartOfAccounts(companyId, createdBy) {
    // ✅ Check if accounts already exist to prevent duplicates
    const existingAccounts = await this.getChartOfAccounts(companyId, true);
    if (existingAccounts.length > 0) {
      console.log('Default accounts already exist, skipping initialization');
      return false;
    }
    
    const defaultAccounts = [
      // ASSETS (1000-1999)
      // Current Assets
      { accountCode: '1000', accountName: 'Cash on Hand', accountNameAr: 'النقدية في الصندوق', accountType: 'asset', category: 'current_asset', normalBalance: 'debit', isActive: true, openingBalance: 0, allowManualEntries: true },
      { accountCode: '1010', accountName: 'Bank Account - Main', accountNameAr: 'الحساب البنكي الرئيسي', accountType: 'asset', category: 'current_asset', normalBalance: 'debit', isActive: true, openingBalance: 0, allowManualEntries: true },
      { accountCode: '1020', accountName: 'Bank Account - Payroll', accountNameAr: 'الحساب البنكي للرواتب', accountType: 'asset', category: 'current_asset', normalBalance: 'debit', isActive: true, openingBalance: 0, allowManualEntries: true },
      { accountCode: '1100', accountName: 'Accounts Receivable', accountNameAr: 'حسابات المدينين', accountType: 'asset', category: 'current_asset', normalBalance: 'debit', isActive: true, openingBalance: 0, allowManualEntries: false },
      { accountCode: '1200', accountName: 'Fuel Inventory', accountNameAr: 'مخزون الوقود', accountType: 'asset', category: 'current_asset', normalBalance: 'debit', isActive: true, openingBalance: 0, allowManualEntries: true },
      { accountCode: '1210', accountName: 'Spare Parts Inventory', accountNameAr: 'مخزون قطع الغيار', accountType: 'asset', category: 'current_asset', normalBalance: 'debit', isActive: true, openingBalance: 0, allowManualEntries: true },
      { accountCode: '1300', accountName: 'VAT Input Tax', accountNameAr: 'ضريبة القيمة المضافة المدفوعة', accountType: 'asset', category: 'current_asset', normalBalance: 'debit', isActive: true, openingBalance: 0, allowManualEntries: false, isVatAccount: true, isTaxRelated: true },
      { accountCode: '1400', accountName: 'Prepaid Insurance', accountNameAr: 'التأمين المدفوع مقدماً', accountType: 'asset', category: 'current_asset', normalBalance: 'debit', isActive: true, openingBalance: 0, allowManualEntries: true },
      { accountCode: '1410', accountName: 'Prepaid Rent', accountNameAr: 'الإيجار المدفوع مقدماً', accountType: 'asset', category: 'current_asset', normalBalance: 'debit', isActive: true, openingBalance: 0, allowManualEntries: true },
      
      // Fixed Assets
      { accountCode: '1500', accountName: 'Trucks - Heavy Duty', accountNameAr: 'الشاحنات الثقيلة', accountType: 'asset', category: 'fixed_asset', normalBalance: 'debit', isActive: true, openingBalance: 0, allowManualEntries: true },
      { accountCode: '1510', accountName: 'Trucks - Medium Duty', accountNameAr: 'الشاحنات المتوسطة', accountType: 'asset', category: 'fixed_asset', normalBalance: 'debit', isActive: true, openingBalance: 0, allowManualEntries: true },
      { accountCode: '1520', accountName: 'Delivery Vans', accountNameAr: 'شاحنات التوصيل', accountType: 'asset', category: 'fixed_asset', normalBalance: 'debit', isActive: true, openingBalance: 0, allowManualEntries: true },
      { accountCode: '1530', accountName: 'Trailers', accountNameAr: 'المقطورات', accountType: 'asset', category: 'fixed_asset', normalBalance: 'debit', isActive: true, openingBalance: 0, allowManualEntries: true },
      { accountCode: '1600', accountName: 'Warehouse Equipment', accountNameAr: 'معدات المستودع', accountType: 'asset', category: 'fixed_asset', normalBalance: 'debit', isActive: true, openingBalance: 0, allowManualEntries: true },
      { accountCode: '1610', accountName: 'Office Equipment', accountNameAr: 'معدات المكتب', accountType: 'asset', category: 'fixed_asset', normalBalance: 'debit', isActive: true, openingBalance: 0, allowManualEntries: true },
      { accountCode: '1620', accountName: 'Computer Equipment', accountNameAr: 'معدات الحاسوب', accountType: 'asset', category: 'fixed_asset', normalBalance: 'debit', isActive: true, openingBalance: 0, allowManualEntries: true },
      { accountCode: '1700', accountName: 'Accumulated Depreciation - Vehicles', accountNameAr: 'مجمع استهلاك المركبات', accountType: 'asset', category: 'fixed_asset', normalBalance: 'credit', isActive: true, openingBalance: 0, allowManualEntries: false },
      { accountCode: '1710', accountName: 'Accumulated Depreciation - Equipment', accountNameAr: 'مجمع استهلاك المعدات', accountType: 'asset', category: 'fixed_asset', normalBalance: 'credit', isActive: true, openingBalance: 0, allowManualEntries: false },
      
      // LIABILITIES (2000-2999)
      // Current Liabilities
      { accountCode: '2000', accountName: 'Accounts Payable', accountNameAr: 'حسابات الدائنين', accountType: 'liability', category: 'current_liability', normalBalance: 'credit', isActive: true, openingBalance: 0, allowManualEntries: false },
      { accountCode: '2100', accountName: 'VAT Output Tax', accountNameAr: 'ضريبة القيمة المضافة المحصلة', accountType: 'liability', category: 'current_liability', normalBalance: 'credit', isActive: true, openingBalance: 0, allowManualEntries: false, isVatAccount: true, isTaxRelated: true },
      { accountCode: '2200', accountName: 'Accrued Salaries', accountNameAr: 'الرواتب المستحقة', accountType: 'liability', category: 'current_liability', normalBalance: 'credit', isActive: true, openingBalance: 0, allowManualEntries: false },
      { accountCode: '2210', accountName: 'Accrued Commission', accountNameAr: 'العمولات المستحقة', accountType: 'liability', category: 'current_liability', normalBalance: 'credit', isActive: true, openingBalance: 0, allowManualEntries: false },
      { accountCode: '2300', accountName: 'GOSI Payable', accountNameAr: 'التأمينات الاجتماعية المستحقة', accountType: 'liability', category: 'current_liability', normalBalance: 'credit', isActive: true, openingBalance: 0, allowManualEntries: true, isTaxRelated: true },
      { accountCode: '2400', accountName: 'Zakat Provision', accountNameAr: 'مخصص الزكاة', accountType: 'liability', category: 'current_liability', normalBalance: 'credit', isActive: true, openingBalance: 0, allowManualEntries: true, isZakatAccount: true, isTaxRelated: true },
      { accountCode: '2500', accountName: 'Vehicle Loans', accountNameAr: 'قروض المركبات', accountType: 'liability', category: 'long_term_liability', normalBalance: 'credit', isActive: true, openingBalance: 0, allowManualEntries: true },
      { accountCode: '2600', accountName: 'Equipment Financing', accountNameAr: 'تمويل المعدات', accountType: 'liability', category: 'long_term_liability', normalBalance: 'credit', isActive: true, openingBalance: 0, allowManualEntries: true },
      
      // EQUITY (3000-3999)
      { accountCode: '3000', accountName: 'Owner\'s Capital', accountNameAr: 'رأس مال المالك', accountType: 'equity', category: 'equity', normalBalance: 'credit', isActive: true, openingBalance: 0, allowManualEntries: true },
      { accountCode: '3100', accountName: 'Retained Earnings', accountNameAr: 'الأرباح المحتجزة', accountType: 'equity', category: 'equity', normalBalance: 'credit', isActive: true, openingBalance: 0, allowManualEntries: false },
      { accountCode: '3200', accountName: 'Current Year Earnings', accountNameAr: 'أرباح السنة الحالية', accountType: 'equity', category: 'equity', normalBalance: 'credit', isActive: true, openingBalance: 0, allowManualEntries: false },
      
      // REVENUE (4000-4999)
      { accountCode: '4000', accountName: 'Transportation Revenue', accountNameAr: 'إيرادات النقل', accountType: 'revenue', category: 'operating_revenue', normalBalance: 'credit', isActive: true, openingBalance: 0, allowManualEntries: false },
      { accountCode: '4100', accountName: 'Warehousing Revenue', accountNameAr: 'إيرادات التخزين', accountType: 'revenue', category: 'operating_revenue', normalBalance: 'credit', isActive: true, openingBalance: 0, allowManualEntries: false },
      { accountCode: '4200', accountName: 'Freight Forwarding Revenue', accountNameAr: 'إيرادات الشحن والتخليص', accountType: 'revenue', category: 'operating_revenue', normalBalance: 'credit', isActive: true, openingBalance: 0, allowManualEntries: false },
      { accountCode: '4300', accountName: 'Customs Clearance Revenue', accountNameAr: 'إيرادات التخليص الجمركي', accountType: 'revenue', category: 'operating_revenue', normalBalance: 'credit', isActive: true, openingBalance: 0, allowManualEntries: false },
      { accountCode: '4400', accountName: 'Insurance Revenue', accountNameAr: 'إيرادات التأمين', accountType: 'revenue', category: 'operating_revenue', normalBalance: 'credit', isActive: true, openingBalance: 0, allowManualEntries: false },
      { accountCode: '4500', accountName: 'Fuel Surcharge Revenue', accountNameAr: 'إيرادات رسوم الوقود', accountType: 'revenue', category: 'operating_revenue', normalBalance: 'credit', isActive: true, openingBalance: 0, allowManualEntries: false },
      { accountCode: '4900', accountName: 'Other Operating Revenue', accountNameAr: 'إيرادات تشغيلية أخرى', accountType: 'revenue', category: 'other_revenue', normalBalance: 'credit', isActive: true, openingBalance: 0, allowManualEntries: true },
      
      // EXPENSES (5000-5999)
      // Operating Expenses
      { accountCode: '5000', accountName: 'Fuel Expenses', accountNameAr: 'مصاريف الوقود', accountType: 'expense', category: 'operating_expense', normalBalance: 'debit', isActive: true, openingBalance: 0, allowManualEntries: false },
      { accountCode: '5100', accountName: 'Vehicle Maintenance', accountNameAr: 'صيانة المركبات', accountType: 'expense', category: 'operating_expense', normalBalance: 'debit', isActive: true, openingBalance: 0, allowManualEntries: false },
      { accountCode: '5110', accountName: 'Oil Changes', accountNameAr: 'تغيير الزيت', accountType: 'expense', category: 'operating_expense', normalBalance: 'debit', isActive: true, openingBalance: 0, allowManualEntries: true },
      { accountCode: '5120', accountName: 'Tire Expenses', accountNameAr: 'مصاريف الإطارات', accountType: 'expense', category: 'operating_expense', normalBalance: 'debit', isActive: true, openingBalance: 0, allowManualEntries: true },
      { accountCode: '5200', accountName: 'Driver Salaries', accountNameAr: 'رواتب السائقين', accountType: 'expense', category: 'operating_expense', normalBalance: 'debit', isActive: true, openingBalance: 0, allowManualEntries: false },
      { accountCode: '5210', accountName: 'Driver Benefits', accountNameAr: 'مزايا السائقين', accountType: 'expense', category: 'operating_expense', normalBalance: 'debit', isActive: true, openingBalance: 0, allowManualEntries: false },
      { accountCode: '5300', accountName: 'Vehicle Insurance', accountNameAr: 'تأمين المركبات', accountType: 'expense', category: 'operating_expense', normalBalance: 'debit', isActive: true, openingBalance: 0, allowManualEntries: true },
      { accountCode: '5400', accountName: 'Vehicle Registration & Permits', accountNameAr: 'تسجيل المركبات والتصاريح', accountType: 'expense', category: 'operating_expense', normalBalance: 'debit', isActive: true, openingBalance: 0, allowManualEntries: true },
      { accountCode: '5500', accountName: 'Warehouse Rent', accountNameAr: 'إيجار المستودع', accountType: 'expense', category: 'operating_expense', normalBalance: 'debit', isActive: true, openingBalance: 0, allowManualEntries: true },
      { accountCode: '5600', accountName: 'Depreciation Expense', accountNameAr: 'مصروف الاستهلاك', accountType: 'expense', category: 'operating_expense', normalBalance: 'debit', isActive: true, openingBalance: 0, allowManualEntries: false },
      { accountCode: '5700', accountName: 'Commission Expenses', accountNameAr: 'مصاريف العمولة', accountType: 'expense', category: 'operating_expense', normalBalance: 'debit', isActive: true, openingBalance: 0, allowManualEntries: false },
      { accountCode: '5800', accountName: 'Customs & Documentation Fees', accountNameAr: 'رسوم جمركية ووثائق', accountType: 'expense', category: 'operating_expense', normalBalance: 'debit', isActive: true, openingBalance: 0, allowManualEntries: true },
      
      // Administrative Expenses
      { accountCode: '6000', accountName: 'Office Rent', accountNameAr: 'إيجار المكتب', accountType: 'expense', category: 'administrative_expense', normalBalance: 'debit', isActive: true, openingBalance: 0, allowManualEntries: true },
      { accountCode: '6100', accountName: 'Office Supplies', accountNameAr: 'مستلزمات المكتب', accountType: 'expense', category: 'administrative_expense', normalBalance: 'debit', isActive: true, openingBalance: 0, allowManualEntries: true },
      { accountCode: '6200', accountName: 'Utilities', accountNameAr: 'المرافق العامة', accountType: 'expense', category: 'administrative_expense', normalBalance: 'debit', isActive: true, openingBalance: 0, allowManualEntries: true },
      { accountCode: '6300', accountName: 'Telephone & Internet', accountNameAr: 'الهاتف والإنترنت', accountType: 'expense', category: 'administrative_expense', normalBalance: 'debit', isActive: true, openingBalance: 0, allowManualEntries: true },
      { accountCode: '6400', accountName: 'Professional Services', accountNameAr: 'الخدمات المهنية', accountType: 'expense', category: 'administrative_expense', normalBalance: 'debit', isActive: true, openingBalance: 0, allowManualEntries: true },
      { accountCode: '6500', accountName: 'Marketing & Advertising', accountNameAr: 'التسويق والإعلان', accountType: 'expense', category: 'administrative_expense', normalBalance: 'debit', isActive: true, openingBalance: 0, allowManualEntries: true },
      { accountCode: '6600', accountName: 'Bank Charges', accountNameAr: 'رسوم بنكية', accountType: 'expense', category: 'administrative_expense', normalBalance: 'debit', isActive: true, openingBalance: 0, allowManualEntries: true },
      { accountCode: '6900', accountName: 'Other Administrative Expenses', accountNameAr: 'مصاريف إدارية أخرى', accountType: 'expense', category: 'administrative_expense', normalBalance: 'debit', isActive: true, openingBalance: 0, allowManualEntries: true }
    ];

    const promises = defaultAccounts.map(account => 
      this.addAccount({
        ...account,
        companyId,
        createdBy,
        description: `Default ${account.accountName} account for logistics business`
      })
    );

    await Promise.all(promises);
    return true;
  },

  // ✅ Cleanup function to remove duplicate accounts
  async cleanupDuplicateAccounts(companyId) {
    try {
      const accountsRef = collection(db, 'chartOfAccounts');
      const q = query(accountsRef, where('companyId', '==', companyId), orderBy('accountCode'), orderBy('createdAt'));
      
      const snapshot = await getDocs(q);
      const accounts = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      
      const seenCodes = new Set();
      const duplicatesToDelete = [];
      
      accounts.forEach(account => {
        if (seenCodes.has(account.accountCode)) {
          duplicatesToDelete.push(account.id);
          console.log(`Marking duplicate account for deletion: ${account.accountCode} (${account.id})`);
        } else {
          seenCodes.add(account.accountCode);
        }
      });
      
      // Delete duplicates
      const deletePromises = duplicatesToDelete.map(accountId => {
        const accountRef = doc(db, 'chartOfAccounts', accountId);
        return deleteDoc(accountRef);
      });
      
      await Promise.all(deletePromises);
      
      console.log(`Cleaned up ${duplicatesToDelete.length} duplicate accounts`);
      return duplicatesToDelete.length;
    } catch (error) {
      console.error('Error cleaning up duplicate accounts:', error);
      throw error;
    }
  },

  /**
   * Clean up duplicate accounts and return clean list
   */
  async getCleanChartOfAccounts(companyId, includeInactive = false) {
    try {
      console.log('🧹 Getting clean chart of accounts for company:', companyId);
      
      // First, clean up any duplicates
      await this.cleanupDuplicateAccounts(companyId);
      
      // Then get the clean list
      return await this.getChartOfAccounts(companyId, includeInactive);
    } catch (error) {
      console.error('❌ Error getting clean chart of accounts:', error);
      throw error;
    }
  },

  // Advanced Features
  async searchAccounts(companyId, searchTerm) {
    const accounts = await this.getChartOfAccounts(companyId, true);
    const term = searchTerm.toLowerCase();
    
    return accounts.filter(account => 
      account.accountCode.includes(term) ||
      account.accountName.toLowerCase().includes(term) ||
      (account.accountNameAr && account.accountNameAr.includes(term)) ||
      (account.description && account.description.toLowerCase().includes(term))
    );
  },

  async getAccountsByCategory(companyId, category) {
    const accounts = await this.getChartOfAccounts(companyId);
    return accounts.filter(acc => acc.category === category);
  },

  async getTrialBalance(companyId, asOfDate = null) {
    const accounts = await this.getChartOfAccounts(companyId, true);
    let totalDebits = 0;
    let totalCredits = 0;
    
    const trialBalance = accounts.map(account => {
      const balance = account.currentBalance || 0;
      let debitBalance = 0;
      let creditBalance = 0;
      
      if (['asset', 'expense'].includes(account.accountType)) {
        // Debit normal balance
        if (balance >= 0) {
          debitBalance = balance;
        } else {
          creditBalance = Math.abs(balance);
        }
      } else {
        // Credit normal balance
        if (balance >= 0) {
          creditBalance = balance;
        } else {
          debitBalance = Math.abs(balance);
        }
      }
      
      totalDebits += debitBalance;
      totalCredits += creditBalance;
      
      return {
        ...account,
        debitBalance,
        creditBalance
      };
    });
    
    return {
      accounts: trialBalance.filter(acc => acc.debitBalance !== 0 || acc.creditBalance !== 0),
      totalDebits,
      totalCredits,
      isBalanced: Math.abs(totalDebits - totalCredits) < 0.01,
      asOfDate: asOfDate || new Date()
    };
  },

  async getAccountAnalysis(companyId, accountId, startDate = null, endDate = null) {
    try {
      const account = await this.getAccount(accountId);
      if (!account) {
        throw new Error('Account not found');
      }
      
      // Get journal entries for this account
      const { journalEntriesService } = await import('./journal-entries');
      const allEntries = await journalEntriesService.getJournalEntries(companyId);
      
      // Filter entries that affect this account
      const accountEntries = allEntries.filter(entry => 
        entry.status === 'posted' && 
        entry.lineItems && 
        entry.lineItems.some(item => item.accountCode === account.accountCode)
      );
      
      // Apply date filters if provided
      let filteredEntries = accountEntries;
      if (startDate || endDate) {
        filteredEntries = accountEntries.filter(entry => {
          const entryDate = entry.date?.toDate?.() || new Date(entry.date);
          if (startDate && entryDate < new Date(startDate)) return false;
          if (endDate && entryDate > new Date(endDate)) return false;
          return true;
        });
      }
      
      // Calculate statistics
      let totalDebits = 0;
      let totalCredits = 0;
      let transactionCount = 0;
      
      const transactions = [];
      
      filteredEntries.forEach(entry => {
        // Skip entries without lineItems
        if (!entry.lineItems || !Array.isArray(entry.lineItems)) {
          console.warn('⚠️ Journal entry missing lineItems:', entry.id);
          return;
        }
        
        entry.lineItems.forEach(item => {
          if (item.accountCode === account.accountCode) {
            totalDebits += item.debit || 0;
            totalCredits += item.credit || 0;
            transactionCount++;
            
            transactions.push({
              date: entry.date,
              description: item.description || entry.description,
              reference: entry.reference,
              debit: item.debit || 0,
              credit: item.credit || 0,
              entryNumber: entry.entryNumber,
              entryId: entry.id
            });
          }
        });
      });
      
      // Sort transactions by date
      transactions.sort((a, b) => {
        const dateA = a.date?.toDate?.() || new Date(a.date);
        const dateB = b.date?.toDate?.() || new Date(b.date);
        return dateB - dateA;
      });
      
      return {
        account,
        totalDebits,
        totalCredits,
        netMovement: totalDebits - totalCredits,
        transactionCount,
        transactions,
        averageTransactionSize: transactionCount > 0 ? (totalDebits + totalCredits) / transactionCount : 0,
        period: {
          startDate: startDate || (transactions.length > 0 ? transactions[transactions.length - 1].date : null),
          endDate: endDate || (transactions.length > 0 ? transactions[0].date : null)
        }
      };
    } catch (error) {
      console.error('Error getting account analysis:', error);
      throw error;
    }
  },

  async getAccountUsageStats(companyId) {
    try {
      const accounts = await this.getChartOfAccounts(companyId, true);
      const { journalEntriesService } = await import('./journal-entries');
      const journalEntries = await journalEntriesService.getJournalEntries(companyId);
      
      const usageStats = {};
      
      // Initialize stats for all accounts
      accounts.forEach(account => {
        usageStats[account.accountCode] = {
          account,
          transactionCount: 0,
          totalAmount: 0,
          lastUsed: null,
          isActive: false
        };
      });
      
      // Calculate usage from journal entries
      journalEntries.forEach(entry => {
        if (entry.status === 'posted' && entry.lineItems) {
          entry.lineItems.forEach(item => {
            if (usageStats[item.accountCode]) {
              usageStats[item.accountCode].transactionCount++;
              usageStats[item.accountCode].totalAmount += (item.debit || 0) + (item.credit || 0);
              usageStats[item.accountCode].isActive = true;
              
              const entryDate = entry.date?.toDate?.() || new Date(entry.date);
              if (!usageStats[item.accountCode].lastUsed || entryDate > usageStats[item.accountCode].lastUsed) {
                usageStats[item.accountCode].lastUsed = entryDate;
              }
            }
          });
        }
      });
      
      return Object.values(usageStats);
    } catch (error) {
      console.error('Error getting account usage stats:', error);
      return [];
    }
  },

  async validateAccountStructure(companyId) {
    const accounts = await this.getChartOfAccounts(companyId, true);
    const issues = [];
    
    // Check for duplicate account codes
    const codes = {};
    accounts.forEach(account => {
      if (codes[account.accountCode]) {
        issues.push({
          type: 'duplicate_code',
          message: `Duplicate account code: ${account.accountCode}`,
          accounts: [codes[account.accountCode], account]
        });
      } else {
        codes[account.accountCode] = account;
      }
    });
    
    // Check for accounts with zero activity and zero balance
    const usageStats = await this.getAccountUsageStats(companyId);
    usageStats.forEach(stat => {
      if (!stat.isActive && (stat.account.currentBalance || 0) === 0 && stat.account.isActive) {
        issues.push({
          type: 'unused_account',
          message: `Account ${stat.account.accountCode} - ${stat.account.accountName} has no activity`,
          account: stat.account
        });
      }
    });
    
    // Check for missing essential accounts
    const essentialAccounts = ['1000', '1100', '2000', '2100', '3000', '4000', '5000'];
    essentialAccounts.forEach(code => {
      if (!codes[code]) {
        issues.push({
          type: 'missing_essential',
          message: `Missing essential account: ${code}`,
          accountCode: code
        });
      }
    });
    
    return {
      isValid: issues.length === 0,
      issues,
      totalAccounts: accounts.length,
      activeAccounts: accounts.filter(acc => acc.isActive).length
    };
  },
  async updateAccountBalance(accountId, amount, transactionType = 'debit') {
    const accountRef = doc(db, 'chartOfAccounts', accountId);
    const account = await getDoc(accountRef);
    
    if (!account.exists()) throw new Error('Account not found');
    
    const accountData = account.data();
    let newBalance = accountData.balance || 0;
    
    // Apply accounting rules for balance calculation
    if (accountData.accountType === 'asset' || accountData.accountType === 'expense') {
      // Assets and expenses increase with debits, decrease with credits
      newBalance = transactionType === 'debit' ? newBalance + amount : newBalance - amount;
    } else {
      // Liabilities, equity, and revenue increase with credits, decrease with debits
      newBalance = transactionType === 'credit' ? newBalance + amount : newBalance - amount;
    }
    
    await updateDoc(accountRef, { 
      balance: newBalance, 
      updatedAt: serverTimestamp() 
    });
    
    return newBalance;
  },

  /**
   * Initialize default chart of accounts for logistics companies
   */
  async initializeDefaultChartOfAccounts(companyId, createdBy = 'system') {
    console.log('🚀 Initializing default chart of accounts for company:', companyId);
    
    try {
      // Check if accounts already exist to prevent duplicates
      const existingAccounts = await this.getChartOfAccounts(companyId, true);
      if (existingAccounts.length > 0) {
        console.log('Default accounts already exist, skipping initialization');
        return false;
      }
      
      // Comprehensive default accounts for Saudi logistics companies
      const defaultAccounts = [
        // ASSETS (1000-1999)
        // Current Assets
        { accountCode: '1000', accountName: 'Cash on Hand', accountNameAr: 'النقدية في الصندوق', accountType: 'asset', category: 'current_asset', normalBalance: 'debit', isActive: true, openingBalance: 0, allowManualEntries: true },
        { accountCode: '1010', accountName: 'Bank Account - Operations', accountNameAr: 'الحساب البنكي التشغيلي', accountType: 'asset', category: 'current_asset', normalBalance: 'debit', isActive: true, openingBalance: 0, allowManualEntries: true },
        { accountCode: '1020', accountName: 'Bank Account - Payroll', accountNameAr: 'الحساب البنكي للرواتب', accountType: 'asset', category: 'current_asset', normalBalance: 'debit', isActive: true, openingBalance: 0, allowManualEntries: true },
        { accountCode: '1030', accountName: 'Petty Cash - Drivers', accountNameAr: 'النقدية الصغيرة للسائقين', accountType: 'asset', category: 'current_asset', normalBalance: 'debit', isActive: true, openingBalance: 0, allowManualEntries: true },
        
        { accountCode: '1100', accountName: 'Accounts Receivable - Customers', accountNameAr: 'حسابات المدينين - العملاء', accountType: 'asset', category: 'current_asset', normalBalance: 'debit', isActive: true, openingBalance: 0, allowManualEntries: false },
        { accountCode: '1110', accountName: 'Freight Receivables', accountNameAr: 'مستحقات الشحن', accountType: 'asset', category: 'current_asset', normalBalance: 'debit', isActive: true, openingBalance: 0, allowManualEntries: false },
        { accountCode: '1120', accountName: 'Driver Advances Receivable', accountNameAr: 'سلف السائقين المستحقة', accountType: 'asset', category: 'current_asset', normalBalance: 'debit', isActive: true, openingBalance: 0, allowManualEntries: true },
        
        { accountCode: '1200', accountName: 'Fuel Inventory', accountNameAr: 'مخزون الوقود', accountType: 'asset', category: 'current_asset', normalBalance: 'debit', isActive: true, openingBalance: 0, allowManualEntries: true },
        { accountCode: '1210', accountName: 'Spare Parts Inventory', accountNameAr: 'مخزون قطع الغيار', accountType: 'asset', category: 'current_asset', normalBalance: 'debit', isActive: true, openingBalance: 0, allowManualEntries: true },
        { accountCode: '1220', accountName: 'Tires Inventory', accountNameAr: 'مخزون الإطارات', accountType: 'asset', category: 'current_asset', normalBalance: 'debit', isActive: true, openingBalance: 0, allowManualEntries: true },
        
        { accountCode: '1300', accountName: 'VAT Input Tax (15%)', accountNameAr: 'ضريبة القيمة المضافة المدفوعة', accountType: 'asset', category: 'current_asset', normalBalance: 'debit', isActive: true, openingBalance: 0, allowManualEntries: false, isVatAccount: true, isTaxRelated: true },
        { accountCode: '1310', accountName: 'Withholding Tax Receivable', accountNameAr: 'ضريبة الاستقطاع المستحقة', accountType: 'asset', category: 'current_asset', normalBalance: 'debit', isActive: true, openingBalance: 0, allowManualEntries: true, isTaxRelated: true },
        
        { accountCode: '1400', accountName: 'Prepaid Vehicle Insurance', accountNameAr: 'تأمين المركبات المدفوع مقدماً', accountType: 'asset', category: 'current_asset', normalBalance: 'debit', isActive: true, openingBalance: 0, allowManualEntries: true },
        { accountCode: '1410', accountName: 'Prepaid Office Rent', accountNameAr: 'إيجار المكتب المدفوع مقدماً', accountType: 'asset', category: 'current_asset', normalBalance: 'debit', isActive: true, openingBalance: 0, allowManualEntries: true },
        
        // Fixed Assets
        { accountCode: '1500', accountName: 'Heavy Duty Trucks', accountNameAr: 'الشاحنات الثقيلة', accountType: 'asset', category: 'fixed_asset', normalBalance: 'debit', isActive: true, openingBalance: 0, allowManualEntries: true },
        { accountCode: '1510', accountName: 'Medium Duty Trucks', accountNameAr: 'الشاحنات المتوسطة', accountType: 'asset', category: 'fixed_asset', normalBalance: 'debit', isActive: true, openingBalance: 0, allowManualEntries: true },
        { accountCode: '1520', accountName: 'Light Delivery Vans', accountNameAr: 'شاحنات التوصيل الخفيفة', accountType: 'asset', category: 'fixed_asset', normalBalance: 'debit', isActive: true, openingBalance: 0, allowManualEntries: true },
        { accountCode: '1530', accountName: 'Trailers & Semi-Trailers', accountNameAr: 'المقطورات ونصف المقطورات', accountType: 'asset', category: 'fixed_asset', normalBalance: 'debit', isActive: true, openingBalance: 0, allowManualEntries: true },
        
        { accountCode: '1600', accountName: 'Warehouse & Storage Equipment', accountNameAr: 'معدات المستودع والتخزين', accountType: 'asset', category: 'fixed_asset', normalBalance: 'debit', isActive: true, openingBalance: 0, allowManualEntries: true },
        { accountCode: '1610', accountName: 'Office Equipment & Furniture', accountNameAr: 'معدات وأثاث المكتب', accountType: 'asset', category: 'fixed_asset', normalBalance: 'debit', isActive: true, openingBalance: 0, allowManualEntries: true },
        { accountCode: '1620', accountName: 'Computer & IT Equipment', accountNameAr: 'معدات الحاسوب وتقنية المعلومات', accountType: 'asset', category: 'fixed_asset', normalBalance: 'debit', isActive: true, openingBalance: 0, allowManualEntries: true },
        
        { accountCode: '1700', accountName: 'Accumulated Depreciation - Vehicles', accountNameAr: 'مجمع استهلاك المركبات', accountType: 'asset', category: 'fixed_asset', normalBalance: 'credit', isActive: true, openingBalance: 0, allowManualEntries: false },
        { accountCode: '1710', accountName: 'Accumulated Depreciation - Equipment', accountNameAr: 'مجمع استهلاك المعدات', accountType: 'asset', category: 'fixed_asset', normalBalance: 'credit', isActive: true, openingBalance: 0, allowManualEntries: false },
        
        // LIABILITIES (2000-2999)
        { accountCode: '2000', accountName: 'Accounts Payable - Suppliers', accountNameAr: 'حسابات الدائنين - الموردين', accountType: 'liability', category: 'current_liability', normalBalance: 'credit', isActive: true, openingBalance: 0, allowManualEntries: false },
        { accountCode: '2010', accountName: 'Fuel Suppliers Payable', accountNameAr: 'مستحقات موردي الوقود', accountType: 'liability', category: 'current_liability', normalBalance: 'credit', isActive: true, openingBalance: 0, allowManualEntries: false },
        
        { accountCode: '2100', accountName: 'VAT Output Tax (15%)', accountNameAr: 'ضريبة القيمة المضافة المحصلة', accountType: 'liability', category: 'current_liability', normalBalance: 'credit', isActive: true, openingBalance: 0, allowManualEntries: false, isVatAccount: true, isTaxRelated: true },
        { accountCode: '2110', accountName: 'VAT Payable to GAZT', accountNameAr: 'ضريبة القيمة المضافة المستحقة للهيئة', accountType: 'liability', category: 'current_liability', normalBalance: 'credit', isActive: true, openingBalance: 0, allowManualEntries: false, isVatAccount: true, isTaxRelated: true },
        
        { accountCode: '2200', accountName: 'Salaries & Wages Payable', accountNameAr: 'الرواتب والأجور المستحقة', accountType: 'liability', category: 'current_liability', normalBalance: 'credit', isActive: true, openingBalance: 0, allowManualEntries: false },
        { accountCode: '2210', accountName: 'Driver Commission Payable', accountNameAr: 'عمولات السائقين المستحقة', accountType: 'liability', category: 'current_liability', normalBalance: 'credit', isActive: true, openingBalance: 0, allowManualEntries: false },
        
        { accountCode: '2300', accountName: 'GOSI Contributions Payable', accountNameAr: 'اشتراكات التأمينات الاجتماعية المستحقة', accountType: 'liability', category: 'current_liability', normalBalance: 'credit', isActive: true, openingBalance: 0, allowManualEntries: true, isTaxRelated: true },
        
        { accountCode: '2400', accountName: 'Zakat Provision (2.5%)', accountNameAr: 'مخصص الزكاة', accountType: 'liability', category: 'current_liability', normalBalance: 'credit', isActive: true, openingBalance: 0, allowManualEntries: true, isZakatAccount: true, isTaxRelated: true },
        { accountCode: '2410', accountName: 'End of Service Benefits', accountNameAr: 'مخصص مكافأة نهاية الخدمة', accountType: 'liability', category: 'current_liability', normalBalance: 'credit', isActive: true, openingBalance: 0, allowManualEntries: true },
        
        { accountCode: '2500', accountName: 'Vehicle Loans & Financing', accountNameAr: 'قروض وتمويل المركبات', accountType: 'liability', category: 'long_term_liability', normalBalance: 'credit', isActive: true, openingBalance: 0, allowManualEntries: true },
        { accountCode: '2510', accountName: 'Equipment Financing', accountNameAr: 'تمويل المعدات', accountType: 'liability', category: 'long_term_liability', normalBalance: 'credit', isActive: true, openingBalance: 0, allowManualEntries: true },
        
        // EQUITY (3000-3999)
        { accountCode: '3000', accountName: 'Owner\'s Capital', accountNameAr: 'رأس مال المالك', accountType: 'equity', category: 'equity', normalBalance: 'credit', isActive: true, openingBalance: 0, allowManualEntries: true },
        { accountCode: '3100', accountName: 'Retained Earnings', accountNameAr: 'الأرباح المحتجزة', accountType: 'equity', category: 'equity', normalBalance: 'credit', isActive: true, openingBalance: 0, allowManualEntries: false },
        { accountCode: '3200', accountName: 'Current Year Earnings', accountNameAr: 'أرباح السنة الحالية', accountType: 'equity', category: 'equity', normalBalance: 'credit', isActive: true, openingBalance: 0, allowManualEntries: false },
        
        // REVENUE (4000-4999)
        { accountCode: '4000', accountName: 'Local Transportation Revenue', accountNameAr: 'إيرادات النقل المحلي', accountType: 'revenue', category: 'operating_revenue', normalBalance: 'credit', isActive: true, openingBalance: 0, allowManualEntries: false },
        { accountCode: '4010', accountName: 'Long Distance Transportation', accountNameAr: 'إيرادات النقل لمسافات طويلة', accountType: 'revenue', category: 'operating_revenue', normalBalance: 'credit', isActive: true, openingBalance: 0, allowManualEntries: false },
        { accountCode: '4020', accountName: 'Express Delivery Revenue', accountNameAr: 'إيرادات التوصيل السريع', accountType: 'revenue', category: 'operating_revenue', normalBalance: 'credit', isActive: true, openingBalance: 0, allowManualEntries: false },
        
        { accountCode: '4100', accountName: 'Warehousing & Storage Revenue', accountNameAr: 'إيرادات التخزين والمستودعات', accountType: 'revenue', category: 'operating_revenue', normalBalance: 'credit', isActive: true, openingBalance: 0, allowManualEntries: false },
        { accountCode: '4110', accountName: 'Loading & Unloading Revenue', accountNameAr: 'إيرادات التحميل والتفريغ', accountType: 'revenue', category: 'operating_revenue', normalBalance: 'credit', isActive: true, openingBalance: 0, allowManualEntries: false },
        
        { accountCode: '4200', accountName: 'International Freight Revenue', accountNameAr: 'إيرادات الشحن الدولي', accountType: 'revenue', category: 'operating_revenue', normalBalance: 'credit', isActive: true, openingBalance: 0, allowManualEntries: false },
        { accountCode: '4210', accountName: 'Customs Clearance Revenue', accountNameAr: 'إيرادات التخليص الجمركي', accountType: 'revenue', category: 'operating_revenue', normalBalance: 'credit', isActive: true, openingBalance: 0, allowManualEntries: false },
        
        { accountCode: '4300', accountName: 'Insurance Services Revenue', accountNameAr: 'إيرادات خدمات التأمين', accountType: 'revenue', category: 'operating_revenue', normalBalance: 'credit', isActive: true, openingBalance: 0, allowManualEntries: false },
        { accountCode: '4400', accountName: 'Fuel Surcharge Revenue', accountNameAr: 'إيرادات رسوم الوقود', accountType: 'revenue', category: 'operating_revenue', normalBalance: 'credit', isActive: true, openingBalance: 0, allowManualEntries: false },
        
        { accountCode: '4900', accountName: 'Other Operating Revenue', accountNameAr: 'إيرادات تشغيلية أخرى', accountType: 'revenue', category: 'other_revenue', normalBalance: 'credit', isActive: true, openingBalance: 0, allowManualEntries: true },
        
        // EXPENSES (5000-6999)
        { accountCode: '5000', accountName: 'Fuel & Diesel Expenses', accountNameAr: 'مصاريف الوقود والديزل', accountType: 'expense', category: 'operating_expense', normalBalance: 'debit', isActive: true, openingBalance: 0, allowManualEntries: false },
        { accountCode: '5010', accountName: 'Vehicle Maintenance & Repairs', accountNameAr: 'صيانة وإصلاح المركبات', accountType: 'expense', category: 'operating_expense', normalBalance: 'debit', isActive: true, openingBalance: 0, allowManualEntries: false },
        { accountCode: '5020', accountName: 'Tire Replacement & Repairs', accountNameAr: 'استبدال وإصلاح الإطارات', accountType: 'expense', category: 'operating_expense', normalBalance: 'debit', isActive: true, openingBalance: 0, allowManualEntries: true },
        { accountCode: '5030', accountName: 'Oil Changes & Lubricants', accountNameAr: 'تغيير الزيت والمواد المزلقة', accountType: 'expense', category: 'operating_expense', normalBalance: 'debit', isActive: true, openingBalance: 0, allowManualEntries: true },
        
        { accountCode: '5100', accountName: 'Driver Salaries & Wages', accountNameAr: 'رواتب وأجور السائقين', accountType: 'expense', category: 'operating_expense', normalBalance: 'debit', isActive: true, openingBalance: 0, allowManualEntries: false },
        { accountCode: '5110', accountName: 'Driver Commission & Bonuses', accountNameAr: 'عمولات ومكافآت السائقين', accountType: 'expense', category: 'operating_expense', normalBalance: 'debit', isActive: true, openingBalance: 0, allowManualEntries: false },
        { accountCode: '5120', accountName: 'Driver Benefits & Allowances', accountNameAr: 'مزايا وبدلات السائقين', accountType: 'expense', category: 'operating_expense', normalBalance: 'debit', isActive: true, openingBalance: 0, allowManualEntries: false },
        
        { accountCode: '5200', accountName: 'Vehicle Insurance Premiums', accountNameAr: 'أقساط تأمين المركبات', accountType: 'expense', category: 'operating_expense', normalBalance: 'debit', isActive: true, openingBalance: 0, allowManualEntries: true },
        { accountCode: '5210', accountName: 'Vehicle Registration & Permits', accountNameAr: 'تسجيل المركبات والتصاريح', accountType: 'expense', category: 'operating_expense', normalBalance: 'debit', isActive: true, openingBalance: 0, allowManualEntries: true },
        
        { accountCode: '5300', accountName: 'Warehouse Rent & Utilities', accountNameAr: 'إيجار المستودع والمرافق', accountType: 'expense', category: 'operating_expense', normalBalance: 'debit', isActive: true, openingBalance: 0, allowManualEntries: true },
        
        { accountCode: '5400', accountName: 'Depreciation - Vehicles', accountNameAr: 'استهلاك المركبات', accountType: 'expense', category: 'operating_expense', normalBalance: 'debit', isActive: true, openingBalance: 0, allowManualEntries: false },
        { accountCode: '5410', accountName: 'Depreciation - Equipment', accountNameAr: 'استهلاك المعدات', accountType: 'expense', category: 'operating_expense', normalBalance: 'debit', isActive: true, openingBalance: 0, allowManualEntries: false },
        
        { accountCode: '5500', accountName: 'Customs Duties & Fees', accountNameAr: 'الرسوم الجمركية', accountType: 'expense', category: 'operating_expense', normalBalance: 'debit', isActive: true, openingBalance: 0, allowManualEntries: true },
        
        // Administrative Expenses (6000-6999)
        { accountCode: '6000', accountName: 'Office Rent & Utilities', accountNameAr: 'إيجار المكتب والمرافق', accountType: 'expense', category: 'administrative_expense', normalBalance: 'debit', isActive: true, openingBalance: 0, allowManualEntries: true },
        { accountCode: '6010', accountName: 'Office Supplies & Stationery', accountNameAr: 'مستلزمات المكتب والقرطاسية', accountType: 'expense', category: 'administrative_expense', normalBalance: 'debit', isActive: true, openingBalance: 0, allowManualEntries: true },
        { accountCode: '6020', accountName: 'Telephone & Internet', accountNameAr: 'الهاتف والإنترنت', accountType: 'expense', category: 'administrative_expense', normalBalance: 'debit', isActive: true, openingBalance: 0, allowManualEntries: true },
        
        { accountCode: '6100', accountName: 'Accounting & Bookkeeping', accountNameAr: 'المحاسبة ومسك الدفاتر', accountType: 'expense', category: 'administrative_expense', normalBalance: 'debit', isActive: true, openingBalance: 0, allowManualEntries: true },
        { accountCode: '6110', accountName: 'Legal & Professional Fees', accountNameAr: 'الرسوم القانونية والمهنية', accountType: 'expense', category: 'administrative_expense', normalBalance: 'debit', isActive: true, openingBalance: 0, allowManualEntries: true },
        
        { accountCode: '6200', accountName: 'Marketing & Advertising', accountNameAr: 'التسويق والإعلان', accountType: 'expense', category: 'administrative_expense', normalBalance: 'debit', isActive: true, openingBalance: 0, allowManualEntries: true },
        
        { accountCode: '6300', accountName: 'Bank Charges & Fees', accountNameAr: 'الرسوم والعمولات البنكية', accountType: 'expense', category: 'administrative_expense', normalBalance: 'debit', isActive: true, openingBalance: 0, allowManualEntries: true },
        
        { accountCode: '6400', accountName: 'Administrative Salaries', accountNameAr: 'رواتب الموظفين الإداريين', accountType: 'expense', category: 'administrative_expense', normalBalance: 'debit', isActive: true, openingBalance: 0, allowManualEntries: false },
        
        { accountCode: '6900', accountName: 'Other Administrative Expenses', accountNameAr: 'مصاريف إدارية أخرى', accountType: 'expense', category: 'administrative_expense', normalBalance: 'debit', isActive: true, openingBalance: 0, allowManualEntries: true }
      ];

      console.log(`Creating ${defaultAccounts.length} default accounts...`);
      
      // Create all default accounts
      const promises = defaultAccounts.map(account => 
        this.addAccount({
          ...account,
          companyId,
          createdBy,
          description: `Default ${account.accountName} account for logistics business`,
          requireDepartment: account.requireDepartment || false,
          isTaxRelated: account.isTaxRelated || false,
          isVatAccount: account.isVatAccount || false,
          isZakatAccount: account.isZakatAccount || false
        })
      );

      await Promise.all(promises);
      
      console.log(`✅ Successfully created ${defaultAccounts.length} default accounts`);
      return true;
      
    } catch (error) {
      console.error('❌ Error initializing default chart of accounts:', error);
      throw error;
    }
  }
}; 